import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cap_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Authenticated file download. `window.open(url)` cannot attach the
 * `Authorization: Bearer <token>` header, so we fetch the file as a blob
 * (auth via the axios interceptor above), then open the object URL in a
 * new tab. Falls back to same-tab download when the popup is blocked.
 * Keeps the backend endpoint fully protected — no public URLs.
 */
export const downloadFile = async (fileId, filename) => {
  const resp = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(resp.data);
  const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
  if (!win) {
    // popup blocked → force a download so user still gets the file
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename || `file-${fileId}`;
    document.body.appendChild(a); a.click(); a.remove();
  }
  // revoke after the tab has a moment to load
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
};

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const money = (n, currency = "CAD") => {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-CA", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

// Convert a raw status token like "in_transit" or "not_selected" into a
// user-friendly Title Case label like "In Transit" / "Not Selected".
// Preserves already-formatted values ("Awarded", "Under Review").
export const formatStatus = (raw) => {
  if (raw == null || raw === "") return "—";
  const s = String(raw).trim();
  if (/[A-Z]/.test(s) && /\s/.test(s)) return s; // already Title Case with spaces
  return s
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

// Normalize any legacy or non-canonical UOM string to the canonical option.
const UOM_MAP = {
  each: "Each", unit: "Each", units: "Each", pcs: "Each", pc: "Each", piece: "Each", pieces: "Each",
  pair: "Pair", pairs: "Pair",
  box: "Box", boxes: "Box",
  case: "Case", cases: "Case",
  pack: "Pack", packs: "Pack",
  set: "Set", sets: "Set",
  kg: "Kg", kgs: "Kg", kilogram: "Kg", kilograms: "Kg",
  litre: "Litre", litres: "Litre", liter: "Litre", liters: "Litre", l: "Litre",
  metre: "Metre", metres: "Metre", meter: "Metre", meters: "Metre", m: "Metre",
  hour: "Hour", hours: "Hour", hr: "Hour", hrs: "Hour",
  day: "Day", days: "Day",
  lot: "Lot", lots: "Lot",
  service: "Service", services: "Service",
  other: "Other",
};
export const canonUom = (u) => {
  if (!u) return "Each";
  const key = String(u).trim().toLowerCase();
  return UOM_MAP[key] || (String(u).charAt(0).toUpperCase() + String(u).slice(1).toLowerCase());
};

// Pluralization for display alongside a quantity, e.g. "200 pairs", "1 pair", "50 boxes", "10 hours".
const PLURAL = {
  Each: ["each", "each"],
  Pair: ["pair", "pairs"],
  Box: ["box", "boxes"],
  Case: ["case", "cases"],
  Pack: ["pack", "packs"],
  Set: ["set", "sets"],
  Kg: ["kg", "kg"],
  Litre: ["litre", "litres"],
  Metre: ["metre", "metres"],
  Hour: ["hour", "hours"],
  Day: ["day", "days"],
  Lot: ["lot", "lots"],
  Service: ["service", "services"],
  Other: ["unit", "units"],
};

export const qtyLabel = (qty, uom) => {
  if (qty == null && uom == null) return "";
  const q = Number(qty ?? 0);
  const canon = canonUom(uom);
  const [singular, plural] = PLURAL[canon] || [canon.toLowerCase(), canon.toLowerCase()];
  const word = q === 1 ? singular : plural;
  return `${q.toLocaleString("en-CA")} ${word}`;
};

// Per-UOM adjective, e.g. "per pair", "per kg", "per each", "per service".
export const perUomLabel = (uom) => {
  const canon = canonUom(uom);
  const singular = (PLURAL[canon] && PLURAL[canon][0]) || canon.toLowerCase();
  return `per ${singular}`;
};
